import React, { useEffect, useState, useMemo } from 'react';
import {
  Image as ImageIcon, Wand2, X, Loader, CheckCircle, AlertCircle,
  Film, Sparkles, RefreshCw, Search
} from 'lucide-react';
import { getCreatives, generateCreative, getProduits } from '../services/api';
import CreativeCanvasPreview from '../components/CreativeCanvasPreview';
import './CreativeStudio.css';

const fmt = (n) => (n || n === 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' XOF' : '—');

const STATUS = {
  pending:   { label: 'En attente', color: '#475569', bg: '#f1f5f9' },
  rendering: { label: 'Rendu…',     color: '#d97706', bg: '#fef3c7' },
  ready:     { label: 'Prête',      color: '#16a34a', bg: '#dcfce7' },
  failed:    { label: 'Échec',      color: '#dc2626', bg: '#fee2e2' },
};

const TRIGGERS = {
  manual: 'Manuelle', 'product.created': 'Nouveau produit', 'price.changed': 'Prix modifié',
};

const StatusBadge = ({ s }) => {
  const st = STATUS[s] || STATUS.pending;
  return <span className="cs-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>;
};

export default function CreativeStudio() {
  const [creatives, setCreatives] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [productId, setProductId] = useState('');
  const [format, setFormat] = useState('png');
  const [search, setSearch] = useState('');
  const [generating, setGenerating] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.format = filter;
      const res = await getCreatives(params);
      setCreatives(res.data.data || []);
    } catch { showToast('error', 'Erreur de chargement des créas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = async () => {
    setShowModal(true);
    if (products.length === 0) {
      try {
        const res = await getProduits({ forSale: true });
        setProducts(res.data.data || res.data || []);
      } catch { showToast('error', 'Impossible de charger les produits'); }
    }
  };

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products.filter((p) =>
      (p.nom || '').toLowerCase().includes(q) || (p.codeProduit || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [products, search]);

  const selectedProduct = useMemo(
    () => products.find((p) => p._id === productId) || null,
    [products, productId]
  );

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!productId) return;
    setGenerating(true);
    try {
      const res = await generateCreative({ productId, format });
      if (res.data.success) showToast('success', 'Créa générée');
      else showToast('error', 'Le rendu a échoué (voir la carte)');
      setShowModal(false);
      setProductId(''); setSearch('');
      load();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de génération');
    } finally { setGenerating(false); }
  };

  return (
    <div className="cs-page">
      {toast && (
        <div className={`cs-toast cs-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{toast.text}</span>
        </div>
      )}

      <div className="cs-header">
        <div>
          <h1 className="cs-title"><Sparkles size={26} /> Créa Studio</h1>
          <p className="cs-subtitle">Génération automatique de visuels (PNG &amp; MP4) depuis le catalogue produits.</p>
        </div>
        <div className="cs-header-actions">
          <button className="cs-btn cs-btn--ghost" onClick={load} title="Rafraîchir"><RefreshCw size={16} /></button>
          <button className="cs-btn cs-btn--primary" onClick={openModal}><Wand2 size={18} /> Générer une créa</button>
        </div>
      </div>

      {/* Filtres */}
      <div className="cs-filters">
        {['all', 'png', 'mp4'].map((f) => (
          <button key={f} className={`cs-chip ${filter === f ? 'cs-chip--active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'Tout' : f.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cs-loading"><Loader className="cs-spin" size={28} /> Chargement…</div>
      ) : creatives.length === 0 ? (
        <div className="cs-empty-hero">
          <div className="cs-empty-preview">
            <CreativeCanvasPreview nom="Votre produit ici" prix={15000} imageUrl={null} size={300} />
          </div>
          <div className="cs-empty-txt">
            <h2>Transformez vos produits en visuels prêts à publier</h2>
            <p>Choisissez un produit du catalogue : GeStock génère automatiquement un visuel (PNG) — et une vidéo (MP4) pour TikTok — à partir de sa photo, son nom et son prix.</p>
            <button className="cs-btn cs-btn--primary" onClick={openModal}><Wand2 size={16} /> Générer ma première créa</button>
          </div>
        </div>
      ) : (
        <div className="cs-grid">
          {creatives.map((c) => (
            <div key={c.id} className="cs-card">
              <div className="cs-preview">
                {c.status === 'ready' && c.url ? (
                  c.format === 'mp4'
                    ? <video src={c.url} controls className="cs-media" />
                    : <img src={c.url} alt={c.productSnapshot?.nom || 'créa'} className="cs-media" loading="lazy" />
                ) : (
                  <div className="cs-preview-empty">
                    {c.status === 'failed' ? <AlertCircle size={28} /> : <Loader className="cs-spin" size={24} />}
                  </div>
                )}
                <span className="cs-format-tag">{c.format === 'mp4' ? <Film size={12} /> : <ImageIcon size={12} />} {c.format.toUpperCase()}</span>
              </div>
              <div className="cs-card-body">
                <div className="cs-card-top">
                  <strong>{c.productSnapshot?.nom || 'Produit'}</strong>
                  <StatusBadge s={c.status} />
                </div>
                <div className="cs-card-price">{fmt(c.productSnapshot?.prix)}</div>
                <div className="cs-card-foot">
                  <span className="cs-trigger">{c.suggested ? '✨ ' : ''}{TRIGGERS[c.triggeredBy] || c.triggeredBy}</span>
                  {c.status === 'ready' && c.url && (
                    <a href={c.url} target="_blank" rel="noreferrer" className="cs-link">Ouvrir</a>
                  )}
                </div>
                {c.status === 'failed' && c.error && <div className="cs-error">{c.error}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal génération */}
      {showModal && (
        <div className="cs-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cs-modal-head">
              <h3><Wand2 size={20} /> Générer une créa</h3>
              <button className="cs-icon-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleGenerate} className="cs-form">
              <div className="cs-form-split">
                <div className="cs-form-left">
                  <label>Produit *</label>
                  <div className="cs-search">
                    <Search size={15} />
                    <input placeholder="Rechercher un produit…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <div className="cs-product-list">
                    {filteredProducts.length === 0 && <div className="cs-muted">Aucun produit</div>}
                    {filteredProducts.map((p) => (
                      <button type="button" key={p._id}
                        className={`cs-product ${productId === p._id ? 'cs-product--sel' : ''}`}
                        onClick={() => setProductId(p._id)}>
                        <span className="cs-product-nom">{p.nom}</span>
                        <span className="cs-product-prix">{fmt(p.prixVente)}</span>
                      </button>
                    ))}
                  </div>

                  <label>Format</label>
                  <div className="cs-format-pick">
                    <button type="button" className={`cs-fmt ${format === 'png' ? 'cs-fmt--sel' : ''}`} onClick={() => setFormat('png')}>
                      <ImageIcon size={18} /> PNG <small>image légère</small>
                    </button>
                    <button type="button" className={`cs-fmt ${format === 'mp4' ? 'cs-fmt--sel' : ''}`} onClick={() => setFormat('mp4')}>
                      <Film size={18} /> MP4 <small>vidéo courte</small>
                    </button>
                  </div>
                </div>

                <div className="cs-form-right">
                  <label>Aperçu live</label>
                  <div className="cs-preview-box">
                    <CreativeCanvasPreview
                      nom={selectedProduct?.nom || 'Sélectionnez un produit'}
                      prix={selectedProduct?.prixVente}
                      imageUrl={selectedProduct?.image || null}
                      size={300}
                    />
                  </div>
                  <p className="cs-preview-note">
                    {format === 'mp4'
                      ? 'Le MP4 anime ce visuel (vidéo courte pour TikTok).'
                      : 'Aperçu indicatif — le rendu final est généré côté serveur.'}
                  </p>
                </div>
              </div>

              <div className="cs-modal-foot">
                <button type="button" className="cs-btn cs-btn--ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="cs-btn cs-btn--primary" disabled={generating || !productId}>
                  {generating ? <Loader className="cs-spin" size={16} /> : <Wand2 size={16} />} Générer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
